import {
    BaseFormFieldProps,
    IconWithVariant as Icon
} from '@auviknetworks/iceberg-ui';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Typography
} from '@mui/material';
import { FontSizes } from 'application/Shared/Constants';
import React from 'react';

import { AuvikSwitch } from '../Switches';
import { FieldSchemaJson } from './Field.type';
import { type FactoryFunction } from './types';

/**
 * Props interface for the FormFieldsGroup component
 */
export interface FormFieldsGroupProps {
    /** Base name for the form field group, used as prefix for child field names */
    name: string;
    /** Component factory function to create form field components */
    Factory: FactoryFunction;
    /** Child form field schemas to be rendered */
    children?: FieldSchemaJson[];
    /** Flag to disable all form fields within the group */
    disabled?: boolean;
    /** Array of form field components */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fields?: any;
    /** Helper text to be displayed as group description */
    helperText?: BaseFormFieldProps['helperText'];
    /** Label text displayed in the group header */
    label?: string;
    /** Whether the field group is required and cannot be collapsed */
    required?: boolean;
    /** Variant style for the form fields */
    variant?: 'success' | 'error';
}

/**
 * FormFieldsGroup component renders a collapsible group of form fields using Material-UI Accordion.
 * It allows users to show/hide a group of related form fields, with the option to make the group
 * permanently expanded if required.
 *
 * @param props - Component props
 * @returns A collapsible form fields group component
 *
 * @example
 * ```tsx
 * <FormFieldsGroup
 *   label="Personal Information"
 *   name="personalInfo"
 *   Factory={FormFieldFactory}
 *   required={true}
 * >
 *   {[
 *     { name: 'firstName', type: 'text' },
 *     { name: 'lastName', type: 'text' }
 *   ]}
 * </FormFieldsGroup>
 * ```
 */
export const FormFieldsGroup = ({
    children = [],
    disabled,
    Factory,
    helperText,
    label,
    name,
    required = false,
    variant
}: FormFieldsGroupProps) => {
    // If the group is required, we don't want to allow the user to collapse it.
    const [expanded, setExpanded] = React.useState<boolean>(true);

    return (
        <Accordion
            sx={{
                '&': {
                    margin: '0 0 24px !important',
                    padding: '0 24px 0',
                    width: '100%'
                },
                // Override default Accordion shadows when closed.
                '&::before': {
                    display: 'none'
                }
            }}
            expanded={expanded}
        >
            <AccordionSummary
                sx={{
                    '&': {
                        cursor: 'auto',
                        minHeight: '64px',
                        padding: 0
                    },
                    '& .MuiAccordionSummary-content': {
                        justifyContent: 'space-between',
                        margin: '20px 0'
                    },
                    margin: 0
                }}
            >
                <Typography
                    variant="h1"
                    style={{
                        fontSize: FontSizes[2],
                        fontWeight: 600
                    }}
                >
                    {label || name}
                </Typography>

                {variant && (
                    <Icon
                        variant="tertiary"
                        src={
                            variant === 'success'
                                ? 'CheckFilled'
                                : 'CloseFilled'
                        }
                    />
                )}

                {!required && !variant && (
                    <AuvikSwitch
                        checked={expanded}
                        onChange={() => setExpanded(!expanded)}
                        size="small"
                        inputProps={{ 'aria-label': 'Skip' }}
                    />
                )}
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    '&': {
                        padding: ' 24px 0 8px'
                    }
                }}
            >
                {helperText && (
                    <Typography component="p" style={{ marginBottom: '24px' }}>
                        {helperText}
                    </Typography>
                )}
                {children.map((schema: FieldSchemaJson) => (
                    <Factory
                        key={schema.name}
                        schema={{
                            ...schema,
                            control: {
                                // We expect the nested schemas to be Auvik controls
                                ...schema.control!,
                                disabled: !expanded || disabled
                            },
                            name: `${name}.${schema.name}`
                        }}
                    />
                ))}
            </AccordionDetails>
        </Accordion>
    );
};
